import { createReducer, ActionType, createAsyncAction } from "typesafe-actions";
import {
  InvoiceItemCategoriesListDto,
  InvoiceItemCategoryDto,
  GetInvoiceItemCategoriesListQuery,
  SaveInvoiceItemCategoryRequest,
  ResultOfInvoiceItemCategoryDto,
  DivisionDto,
} from "Api/Api";

export type InvoiceItemCategoriesState = {
  list: InvoiceItemCategoriesListDto;
  detail: InvoiceItemCategoryDto | null;
  divisions: DivisionDto[];
  isLoading: boolean;
  isUpdating: boolean;
  saveSuccess: boolean;
  errorCode: string | null;
};

export const getInvoiceItemCategoriesAsync = createAsyncAction(
  "@invoiceItemCategories/GET_LIST_REQUEST",
  "@invoiceItemCategories/GET_LIST_SUCCESS",
  "@invoiceItemCategories/GET_LIST_FAILURE",
)<GetInvoiceItemCategoriesListQuery, InvoiceItemCategoriesListDto, Error>();

export const getInvoiceItemCategoryDetailAsync = createAsyncAction(
  "@invoiceItemCategories/GET_DETAIL_REQUEST",
  "@invoiceItemCategories/GET_DETAIL_SUCCESS",
  "@invoiceItemCategories/GET_DETAIL_FAILURE",
)<number, InvoiceItemCategoryDto, Error>();

export const saveInvoiceItemCategoryAsync = createAsyncAction(
  "@invoiceItemCategories/SAVE_REQUEST",
  "@invoiceItemCategories/SAVE_SUCCESS",
  "@invoiceItemCategories/SAVE_FAILURE",
)<SaveInvoiceItemCategoryRequest, ResultOfInvoiceItemCategoryDto, Error>();

export const getDivisionsAsync = createAsyncAction(
  "@invoiceItemCategories/GET_DIVISIONS_REQUEST",
  "@invoiceItemCategories/GET_DIVISIONS_SUCCESS",
  "@invoiceItemCategories/GET_DIVISIONS_FAILURE",
)<void, DivisionDto[], Error>();

export type InvoiceItemCategoriesAction =
  | ActionType<typeof getInvoiceItemCategoriesAsync>
  | ActionType<typeof getInvoiceItemCategoryDetailAsync>
  | ActionType<typeof saveInvoiceItemCategoryAsync>
  | ActionType<typeof getDivisionsAsync>;

export const invoiceItemCategoriesReducer = createReducer<
  InvoiceItemCategoriesState,
  InvoiceItemCategoriesAction
>({
  list: {
    count: 0,
    invoiceItemCategories: [],
  },
  detail: null,
  divisions: [],
  isLoading: false,
  isUpdating: false,
  saveSuccess: false,
  errorCode: null,
})
  .handleAction(getInvoiceItemCategoriesAsync.request, state => {
    return { ...state, isLoading: true };
  })
  .handleAction(getInvoiceItemCategoriesAsync.success, (state, action) => {
    return {
      ...state,
      list: action.payload,
      errorCode: null,
      isLoading: false,
    };
  })
  .handleAction(getInvoiceItemCategoriesAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
      isLoading: false,
    };
  })

  .handleAction(getInvoiceItemCategoryDetailAsync.request, state => {
    return { ...state, isLoading: true, saveSuccess: false };
  })
  .handleAction(getInvoiceItemCategoryDetailAsync.success, (state, action) => {
    return {
      ...state,
      detail: action.payload,
      isLoading: false,
    };
  })
  .handleAction(getInvoiceItemCategoryDetailAsync.failure, (state, action) => {
    return {
      ...state,
      isLoading: false,
      errorCode: action.payload.message,
    };
  })

  .handleAction(saveInvoiceItemCategoryAsync.request, state => {
    return { ...state, isUpdating: true, saveSuccess: false };
  })
  .handleAction(saveInvoiceItemCategoryAsync.success, (state, action) => {
    return {
      ...state,
      isUpdating: false,
      saveSuccess: action.payload.isValid,
    };
  })
  .handleAction(saveInvoiceItemCategoryAsync.failure, (state, action) => {
    return {
      ...state,
      isUpdating: false,
      saveSuccess: false,
      errorCode: action.payload.message,
    };
  })

  .handleAction(getDivisionsAsync.request, state => {
    return { ...state, saveSuccess: false };
  })
  .handleAction(getDivisionsAsync.success, (state, action) => {
    return {
      ...state,
      divisions: action.payload,
    };
  })
  .handleAction(getDivisionsAsync.failure, (state, action) => {
    return {
      ...state,
      errorCode: action.payload.message,
    };
  });
